import React, { useMemo } from 'react'
import { SalesStage } from 'models/sales_stages'
import getStageColor from 'utils/getStageColor'
import {
  DEFAULT_STAGE_COLOR,
  getPipelineColorForStage,
  isTerminalStage,
  sortStagesPipelineOrder,
  INACTIVE_STAGE_COLOR,
} from 'components/SalesStage/stageColors'
import { useStageColorMode } from 'components/SalesStage/StageColorContext'
import { usePreferences } from 'context/preferences/PreferencesContext'
import { colors } from 'styles/tokens'

interface SalesStageProgressProps {
  currentStageId?: number
  salesStages?: SalesStage[]
  onStageChange?: (newStageId: number) => void
}

/**
 * Displays a visual progress indicator for sales stages.
 * Shows previous (80% opacity), current (full color), and next (gray) stages.
 * Terminal stages (Closed Won, Closed Lost, Churn) show celebration emojis.
 * When no stage is set, shows "No Stage" followed by the first available stage.
 */
export function SalesStageProgress({
  currentStageId,
  salesStages,
  onStageChange,
}: SalesStageProgressProps) {
  const { isSettingEnabled } = usePreferences()
  const colorMode = useStageColorMode()

  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  // Sort stages using canonical pipeline order (matches color assignment)
  const sortedStages = useMemo(() => {
    if (!salesStages) return []
    if (colorMode === 'pipeline') {
      return sortStagesPipelineOrder(salesStages, customOrderEnabled)
    }
    return [...salesStages].sort(
      (a, b) => (a.win_rate || 0) - (b.win_rate || 0)
    )
  }, [salesStages, customOrderEnabled, colorMode])

  /** Get color + text for a stage using the unified pipeline color function. */
  const getChevronColors = (
    stage: SalesStage
  ): { bg: string; text: string } => {
    if ((colorMode === 'pipeline' || isTerminalStage(stage)) && salesStages) {
      return (
        getPipelineColorForStage(stage.id, salesStages, customOrderEnabled) ?? {
          bg: DEFAULT_STAGE_COLOR,
          text: colors['fg'],
        }
      )
    }
    const [bg, text] = getStageColor(stage.win_rate || 0)
    return { bg, text }
  }

  // Handle "No Stage" state
  if (!currentStageId || sortedStages.length === 0) {
    const firstRealStage = sortedStages[0]

    return (
      <div className="flex flex-1 items-center">
        <ChevronStage
          label="No Stage"
          color={colors['fg-secondary']}
          textColor={colors['fg-inverse']}
          isFirst={true}
          isCurrent={true}
          isLast={!firstRealStage}
          fullWidth={true}
        />
        {firstRealStage && (
          <div style={{ marginLeft: '-6px' }}>
            <ChevronStage
              label={firstRealStage.stage}
              color={INACTIVE_STAGE_COLOR}
              textColor={colors['fg']}
              isFirst={false}
              isCurrent={false}
              isNext={true}
              onClick={() => onStageChange?.(firstRealStage.id)}
            />
          </div>
        )}
      </div>
    )
  }

  // Find current stage index
  const currentIndex = sortedStages.findIndex((s) => s.id === currentStageId)
  if (currentIndex === -1) return null

  const currentStage = sortedStages[currentIndex]
  const currentIsTerminal = isTerminalStage(currentStage)

  // Find previous non-terminal stage (shared for both terminal and regular stages)
  let previousStage: SalesStage | null = null
  let nextStage: SalesStage | null = null

  for (let i = currentIndex - 1; i >= 0; i--) {
    if (!isTerminalStage(sortedStages[i])) {
      previousStage = sortedStages[i]
      break
    }
  }

  // Only non-terminal stages have a next stage
  if (!currentIsTerminal) {
    for (let i = currentIndex + 1; i < sortedStages.length; i++) {
      if (!isTerminalStage(sortedStages[i])) {
        nextStage = sortedStages[i]
        break
      }
    }
  }

  // Only Closed Won gets celebration emojis
  const showCelebration = currentStage.stage.toLowerCase() === 'closed won'

  // Get colors for current stage
  const { bg: currentColor, text: currentTextColor } =
    getChevronColors(currentStage)

  // Convert hex to rgba with 80% opacity for previous stage
  const addOpacity = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r}, ${g}, ${b}, ${alpha})`
  }

  // Previous stage color: in pipeline mode use its own color at 80% opacity;
  // in win_rate mode use the current stage's color at 80% (original behavior)
  const previousColors = previousStage ? getChevronColors(previousStage) : null
  const previousChevronColor = previousColors
    ? addOpacity(
        colorMode === 'pipeline' ? previousColors.bg : currentColor,
        0.8
      )
    : currentColor
  const previousChevronText = previousColors
    ? colorMode === 'pipeline'
      ? previousColors.text
      : currentTextColor
    : currentTextColor

  return (
    <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
      {/* Previous Stage - 80% opacity, clickable */}
      {previousStage && (
        <ChevronStage
          label={previousStage.stage}
          color={previousChevronColor}
          textColor={previousChevronText}
          isFirst={true}
          isCurrent={false}
          isPrevious={true}
          onClick={() => onStageChange?.(previousStage.id)}
        />
      )}

      {/* Current Stage - Full color, bold, not clickable */}
      <div
        className={currentIsTerminal ? 'flex-1' : ''}
        style={{ marginLeft: previousStage ? '-6px' : '0' }}
      >
        <ChevronStage
          label={
            showCelebration
              ? `${currentStage.stage}  🎉 🎉 🎉`
              : currentStage.stage
          }
          color={currentColor}
          textColor={currentTextColor}
          isFirst={!previousStage}
          isCurrent={true}
          isLast={currentIsTerminal}
        />
      </div>

      {/* Next Stage - Gray, clickable */}
      {!currentIsTerminal && nextStage && (
        <div className="flex-1" style={{ marginLeft: '-6px' }}>
          <ChevronStage
            label={nextStage.stage}
            color={INACTIVE_STAGE_COLOR}
            textColor={colors['fg']}
            isFirst={false}
            isCurrent={false}
            isNext={true}
            onClick={() => onStageChange?.(nextStage.id)}
          />
        </div>
      )}
    </div>
  )
}

interface ChevronStageProps {
  label: string
  color: string
  textColor: string
  isFirst: boolean
  isCurrent: boolean
  isLast?: boolean
  fullWidth?: boolean
  isPrevious?: boolean
  isNext?: boolean
  onClick?: () => void
}

/**
 * Left inset notch component
 */
function LeftChevronNotch({
  color,
  isNext = false,
  isPrevious = false,
}: {
  color: string
  isNext?: boolean
  isPrevious?: boolean
}) {
  return (
    <svg
      width="8"
      height="24"
      viewBox="0 0 8 24"
      className={`flex-shrink-0 transition-colors ${
        isNext ? 'group-hover:fill-[#C4C4C4]' : ''
      } ${isPrevious ? 'group-hover:opacity-90' : ''}`}
    >
      <path d="M8 0 L8 24 L0 24 L8 12 L0 0 Z" fill={color} />
    </svg>
  )
}

/**
 * Right pointing chevron component
 */
function RightChevronPoint({
  color,
  isNext = false,
  isPrevious = false,
}: {
  color: string
  isNext?: boolean
  isPrevious?: boolean
}) {
  return (
    <svg
      width="8"
      height="24"
      viewBox="0 0 8 24"
      className={`flex-shrink-0 transition-colors ${
        isNext ? 'group-hover:fill-[#C4C4C4]' : ''
      } ${isPrevious ? 'group-hover:opacity-90' : ''}`}
    >
      <path d="M 0 0 L 8 12 L 0 24 Z" fill={color} />
    </svg>
  )
}

/**
 * Individual chevron-shaped stage indicator.
 * Composed of: [optional left notch] + main body + [optional right point]
 */
function ChevronStage({
  label,
  color,
  textColor,
  isFirst,
  isCurrent,
  isLast = false,
  fullWidth = false,
  isPrevious = false,
  isNext = false,
  onClick,
}: ChevronStageProps) {
  const isClickable = (isPrevious || isNext) && onClick

  return (
    <div
      className={`flex items-center ${fullWidth ? 'flex-1' : ''} ${
        isClickable ? 'cursor-pointer group' : ''
      }`}
      style={{ height: '24px' }}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
    >
      {/* Left notch - not for first item */}
      {!isFirst && (
        <LeftChevronNotch
          color={color}
          isNext={isNext}
          isPrevious={isPrevious}
        />
      )}

      {/* Main body with text */}
      <div
        className={`flex items-center justify-center px-2 transition-colors ${
          fullWidth ? 'flex-1' : ''
        } ${isNext ? 'group-hover:bg-fg-muted' : ''} ${
          isPrevious ? 'group-hover:opacity-90' : ''
        }`}
        style={{ backgroundColor: color, height: '24px' }}
      >
        <span
          className={`whitespace-nowrap text-xxs leading-[1.8em] ${
            isCurrent ? 'font-semibold' : 'font-light'
          }`}
          style={{ color: textColor }}
        >
          {label}
        </span>
      </div>

      {/* Right point - not for last item */}
      {!isLast && (
        <RightChevronPoint
          color={color}
          isNext={isNext}
          isPrevious={isPrevious}
        />
      )}
    </div>
  )
}
